import React, { useState, useRef, useEffect } from 'react';
import useLang from '../../i18n';
import { GitBranch, ChevronDown, Check, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function BranchSwitcher() {
  const { t } = useLang();
  const { user, activeBranch, switchBranch } = useAuthStore();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const branches = user?.branches || [];

  // Auto-set first branch on mount
  useEffect(() => {
    if (!activeBranch && branches.length > 0) {
      const main = branches.find(b => b.isMain) || branches[0];
      switchBranch(main);
    }
  }, []);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const handleSwitch = (branch) => {
    if (activeBranch?._id === branch._id) { setOpen(false); return; }
    
    switchBranch(branch);
    setOpen(false);

    // ── KEY FIX: invalidate ALL queries so data reloads for new branch ──
    qc.invalidateQueries();
    
    toast.success(`شعبه ${branch.name} انتخاب شد`);
  };

  if (!branches.length) {
    return (
      <button onClick={() => navigate('/branches')}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 font-bold">
        <GitBranch className="w-3.5 h-3.5" />
        شعبه تعریف نشده
      </button>
    );
  }

  const current = activeBranch || branches[0];

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl text-xs font-bold text-blue-700 transition-all select-none min-w-[140px]">
        <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 truncate text-right">{current?.name || t('branch')}</span>
        {branches.length > 1 && (
          <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 bg-white border border-gray-200 rounded-xl shadow-xl z-[300] min-w-[220px] overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('branch')}</p>
          </div>
          <div className="py-1 max-h-60 overflow-y-auto">
            {branches.map(branch => {
              const isActive = current?._id === branch._id;
              return (
                <button key={branch._id} onClick={() => handleSwitch(branch)}
                  className={`w-full flex items-center justify-between px-4 py-3 transition-colors text-right
                    ${isActive ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0
                      ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {branch.code?.slice(0, 2) || branch.name?.charAt(0)}
                    </div>
                    <div>
                      <p className={`text-xs font-bold ${isActive ? 'text-blue-700' : 'text-gray-800'}`}>
                        {branch.name}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[10px] text-gray-400 font-mono">{branch.code}</span>
                        {branch.isMain && (
                          <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded font-bold">{t('active')}</span>
                        )}
                        {!branch.isActive && (
                          <span className="text-[9px] bg-gray-100 text-gray-500 px-1 rounded">{t('inactive')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isActive && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          {['admin', 'superadmin'].includes(user?.role) && (
            <div className="border-t border-gray-100 p-2">
              <button onClick={() => { navigate('/branches'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg font-semibold">
                <Plus className="w-3.5 h-3.5" /> مدیریت شعبات
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
